/**
 * Modelli dei LLM interni, configurabili al posto delle variabili d'ambiente
 * (F-064).
 *
 * ## Che cosa questa pagina NON governa
 *
 * I modelli di TradingAgents. Vivono nel servizio `ai-analysis/`, sono
 * configurati là e cambiarli da qui non avrebbe alcun effetto. Sta scritto in
 * pagina e non solo nel codice, perché è l'equivoco che questa schermata invita
 * naturalmente a fare.
 *
 * ## Perché il livello «complex» ha un vincolo in più
 *
 * È quello che serve il briefing di portafoglio, l'unico percorso che usa gli
 * output strutturati. Un modello che non li supporta non fallisce «a volte»:
 * fallisce sempre, e lo fa dentro un job schedulato, dove nessuno collega il
 * guasto alla scelta fatta qui. Per questo, se il catalogo lo dichiara
 * incompatibile, il salvataggio viene rifiutato dal server.
 */

import { useEffect, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { formatDateTime, formatNumber } from '@/lib/format';
import { Alert, Badge, Button, Card, SearchableSelect, Spinner, TableWrap } from '@/ui';
import {
  useLlmModels,
  useRefreshLlmCatalog,
  useUpdateLlmModels,
  type CatalogModel,
  type TierSetting,
} from './api';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

const TIER_LABEL: Record<TierSetting['tier'], string> = {
  complex: 'Complex — ragionamento e output strutturati',
  fast: 'Fast — risposte brevi e frequenti',
  daily_pick: 'Titolo del giorno — ricerca sul web',
  tradingagents_deep: 'TradingAgents · deep — il ragionamento lungo',
  tradingagents_quick: 'TradingAgents · quick — i passaggi brevi',
  autotrade_check_fallback: 'Verifica auto-trading · ripiego — il secondo tentativo',
  agent: 'Studio agentico — chiama i nostri strumenti',
};

const TIER_HINT: Record<TierSetting['tier'], string> = {
  complex: 'Usato dal briefing di portafoglio e dalla verifica delle proposte di auto-trading.',
  fast: 'Usato dagli arricchimenti brevi (riepiloghi, testi di notifica).',
  daily_pick:
    "Non è un terzo punto sull'asse «quanto ragiona»: è un asse diverso — quante ricerche fa e se apre le pagine invece di fermarsi agli estratti.",
  tradingagents_deep:
    "Vuoto = decide il servizio ai-analysis con la propria configurazione. Impostarlo qui evita di modificare un file .env e riavviare il container.",
  tradingagents_quick:
    'Vuoto = decide il servizio ai-analysis. È il modello dei passaggi frequenti del grafo: pesa sul costo più di quanto sembri.',
  autotrade_check_fallback:
    'Vuoto = un tentativo solo. Usato quando il modello «complex» non risponde (timeout, corpo interrotto): sceglierne uno servito da un provider diverso, perché un ripiego identico al principale ripete lo stesso guasto.',
  agent:
    'Un asse diverso da «quanto ragiona»: quanto bene il modello chiama strumenti (argomenti validi, niente giri a vuoto). Serve un modello che dichiari il tool calling; la conclusione dello studio usa «complex».',
};

/** I livelli in cui «nessun modello» è una scelta valida (F-124). */
const OPTIONAL_TIERS: ReadonlySet<TierSetting['tier']> = new Set<TierSetting['tier']>([
  'tradingagents_deep',
  'tradingagents_quick',
  // F-131: senza ripiego la verifica resta a un tentativo, che è il
  // comportamento di sempre — non una configurazione mancante.
  'autotrade_check_fallback',
]);

/**
 * Il prezzo arriva in dollari **per token**: mostrarlo così com'è produce
 * `0.00000015`, che non si legge e non si confronta. Per milione di token è
 * l'unità in cui i provider stessi pubblicano i listini.
 *
 * La moltiplicazione per un milione è l'unico calcolo fatto su questo valore, ed
 * è a sola presentazione: nessun importo del dominio deriva da qui.
 */
function pricePerMillion(value: string | null): string {
  if (value === null) return '—';
  const perMillion = Number.parseFloat(value) * 1_000_000;
  if (!Number.isFinite(perMillion)) return '—';
  if (perMillion === 0) return 'gratis';
  return `$${perMillion.toFixed(perMillion < 1 ? 3 : 2)}`;
}

function CatalogRow({ model }: { model: CatalogModel }) {
  return (
    <tr>
      <td>
        <code>{model.modelId}</code>
        <div className="admin__muted">{model.name}</div>
      </td>
      <td className="numeric">{model.contextLength ? formatNumber(model.contextLength) : '—'}</td>
      <td className="numeric">{pricePerMillion(model.promptPrice)}</td>
      <td className="numeric">{pricePerMillion(model.completionPrice)}</td>
      <td>
        {model.supportsStructuredOutput ? (
          <Badge tone="positive">Sì</Badge>
        ) : (
          <Badge tone="neutral">No</Badge>
        )}
      </td>
      <td>{model.isFree && <Badge tone="warning">Gratuito</Badge>}</td>
    </tr>
  );
}

export function AdminLlmModelsPage() {
  const models = useLlmModels();
  const update = useUpdateLlmModels();
  const refresh = useRefreshLlmCatalog();

  // ⚠️ Una MAPPA per livello, non una variabile per livello. Con due `useState`
  // cablati il terzo livello (`daily_pick`, F-120) veniva reso con il valore di
  // `fast` e salvato sopra di esso: un difetto silenzioso, comparso appena il
  // backend ha smesso di esporre solo due livelli. Aggiungerne uno ora non
  // richiede di toccare questo componente.
  const [draft, setDraft] = useState<Record<string, string>>({});

  // I campi partono dai valori in vigore. Dipendono dai dati caricati, quindi
  // l'allineamento avviene quando arrivano — non a ogni render, altrimenti
  // riscriverebbe ciò che l'amministratore sta selezionando.
  useEffect(() => {
    if (!models.data) return;
    setDraft(Object.fromEntries(models.data.tiers.map((tier) => [tier.tier, tier.modelId ?? ''])));
  }, [models.data]);

  if (models.isPending) return <Spinner />;
  if (models.error) {
    return (
      <Alert variant="error" title="Configurazione non caricata">
        {errorMessage(models.error, 'Errore imprevisto')}
      </Alert>
    );
  }

  const data = models.data;
  const catalogEmpty = data.catalog.length === 0;

  /**
   * Il modello in vigore può non essere in catalogo (catalogo mai aggiornato, o
   * modello ritirato). Va comunque offerto come opzione, altrimenti la select
   * mostrerebbe un valore diverso da quello realmente attivo — cioè mentirebbe
   * proprio sulla cosa che questa pagina serve a leggere.
   */
  const optionsFor = (current: string, allowEmpty = false) => {
    const options = data.catalog.map((model) => ({
      value: model.modelId,
      label: `${model.modelId}${model.isFree ? ' · gratuito' : ''}${model.supportsStructuredOutput ? '' : ' · no structured output'}`,
    }));
    if (current && !data.catalog.some((model) => model.modelId === current)) {
      options.unshift({ value: current, label: `${current} · non in catalogo` });
    }
    // Senza questa voce l'override non sarebbe **removibile** dall'interfaccia:
    // si potrebbe metterlo e non toglierlo.
    if (allowEmpty) {
      options.unshift({ value: '', label: '— nessun override: decide il servizio ai-analysis' });
    }
    return options;
  };

  const dirty = data.tiers.some((tier) => (draft[tier.tier] ?? '') !== (tier.modelId ?? ''));
  // I livelli obbligatori devono avere un valore; quelli opzionali possono
  // essere vuoti, ed è il modo per togliere l'override.
  const complete = data.tiers.every(
    (tier) => OPTIONAL_TIERS.has(tier.tier) || (draft[tier.tier] ?? '') !== '',
  );

  return (
    <>
      {!data.llmEnabled && (
        <Alert variant="warning" title="Il connettore LLM è spento">
          <code>LLM_ENABLED</code> è <code>false</code> o manca <code>OPENROUTER_API_KEY</code>. La
          configurazione qui sotto si può salvare, ma nessuna chiamata partirà finché il connettore
          resta spento.
        </Alert>
      )}

      <Alert variant="info" title="Questa pagina non configura TradingAgents">
        I modelli usati dalle analisi AI dei singoli titoli vivono nel servizio{' '}
        <code>ai-analysis</code>, hanno la loro configurazione e non passano da qui. Quanto segue
        riguarda i LLM interni della piattaforma: briefing di portafoglio, verifica delle proposte di
        auto-trading, riepiloghi.
      </Alert>

      <Card
        title="Modelli in uso"
        subtitle="Senza una riga salvata resta in vigore la variabile d'ambiente corrispondente. I due livelli di TradingAgents fanno eccezione: vuoti, decide il servizio ai-analysis."
      >
        <div className="admin__tiers">
          {data.tiers.map((tier) => (
            <div key={tier.tier} className="admin__tier">
              {/* Ricercabile e non una tendina nativa: il catalogo OpenRouter
                  supera i trecento modelli, e questo è il campo che decide chi
                  ragiona su denaro vero — non deve essere il posto in cui si
                  sbaglia riga scorrendo a occhio. */}
              <SearchableSelect
                label={TIER_LABEL[tier.tier]}
                hint={TIER_HINT[tier.tier]}
                value={draft[tier.tier] ?? ''}
                disabled={catalogEmpty && !tier.modelId}
                placeholder="Cerca un modello…"
                onChange={(next) => setDraft((current) => ({ ...current, [tier.tier]: next }))}
                options={optionsFor(draft[tier.tier] ?? '', OPTIONAL_TIERS.has(tier.tier))}
              />
              <p className="admin__muted">
                {tier.modelId === '' ? (
                  // ⚠️ Vuoto qui non è «nessun modello», è «non lo decidiamo
                  // noi»: dirlo così evita che sembri una configurazione rotta.
                  <>
                    In vigore: <em>quello del servizio ai-analysis</em>{' '}
                    <Badge tone="neutral">nessun override</Badge>
                  </>
                ) : (
                  <>
                    In vigore: <code>{tier.modelId}</code>{' '}
                    {tier.source === 'environment' ? (
                      <Badge tone="warning">da variabile d'ambiente</Badge>
                    ) : (
                      <Badge tone="accent">configurato</Badge>
                    )}
                    {!tier.inCatalog && <> · non presente nel catalogo scaricato</>}
                  </>
                )}
                {tier.updatedAt && <> · aggiornato il {formatDateTime(tier.updatedAt)}</>}
              </p>
              {tier.environmentModelId !== '' && (
                <p className="admin__muted">
                  Valore d'ambiente: <code>{tier.environmentModelId}</code>
                </p>
              )}
            </div>
          ))}
        </div>

        {update.error !== null && (
          <Alert variant="error" title="Salvataggio non riuscito">
            {errorMessage(update.error, 'Errore imprevisto')}
          </Alert>
        )}
        {update.isSuccess && !dirty && (
          <Alert variant="info">Configurazione salvata: ha effetto dalla prossima chiamata.</Alert>
        )}

        <div className="admin__modal-actions">
          <Button
            loading={update.isPending}
            disabled={!dirty || !complete}
            onClick={() => update.mutate(draft)}
          >
            Salva
          </Button>
        </div>
      </Card>

      <Card
        title="Catalogo OpenRouter"
        subtitle={
          data.catalogRefreshedAt
            ? `${formatNumber(data.catalog.length)} modelli · aggiornato il ${formatDateTime(data.catalogRefreshedAt)}`
            : 'Mai scaricato.'
        }
        action={
          <Button variant="secondary" loading={refresh.isPending} onClick={() => refresh.mutate()}>
            Aggiorna elenco
          </Button>
        }
        flush
      >
        {refresh.error !== null && (
          <div className="admin__padded">
            <Alert variant="error" title="Aggiornamento non riuscito">
              {errorMessage(refresh.error, 'Errore imprevisto')}
            </Alert>
          </div>
        )}
        {refresh.isSuccess && (
          <div className="admin__padded">
            <Alert variant="info">
              {formatNumber(refresh.data.total)} modelli in catalogo · {formatNumber(refresh.data.added)}{' '}
              nuovi · {formatNumber(refresh.data.removed)} non più offerti e rimossi.
            </Alert>
          </div>
        )}

        {catalogEmpty ? (
          <div className="admin__padded">
            <p>
              Il catalogo non è mai stato scaricato. «Aggiorna elenco» interroga l'endpoint pubblico
              di OpenRouter: non serve la chiave API, quindi si può fare anche prima di configurare il
              connettore.
            </p>
          </div>
        ) : (
          <TableWrap tall>
            <table className="table table--compact table--sticky-head">
              <thead>
                <tr>
                  <th>Modello</th>
                  <th className="numeric">Contesto</th>
                  <th className="numeric">Input / M token</th>
                  <th className="numeric">Output / M token</th>
                  <th>Output strutturati</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.catalog.map((model) => (
                  <CatalogRow key={model.modelId} model={model} />
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>
    </>
  );
}
